import { useState } from "react";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip, fmtDate } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

function ReferralsTab() {
  const { data, loading, error, refetch } = useFetch("/admin/referrals", []);
  return loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
    !data?.length ? <SectionCard className="p-2"><EmptyState label="No referrals yet" /></SectionCard> : (
      <SectionCard className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="referrals-table">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr><th className="px-4 py-2.5 font-semibold">Code</th><th className="px-4 py-2.5 font-semibold">Referrer</th><th className="px-4 py-2.5 font-semibold">Referred</th><th className="px-4 py-2.5 font-semibold">Status</th><th className="px-4 py-2.5 font-semibold">Created</th></tr>
            </thead>
            <tbody className="divide-y">
              {data.map((r) => (
                <tr key={r.id} className="hover:bg-muted/30" data-testid={`referral-${r.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.referral_code}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.referrer_id?.slice(-6)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.referred_id?.slice(-6)}</td>
                  <td className="px-4 py-2.5"><StatusChip status={r.status} /></td>
                  <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    );
}

function LoyaltyTab() {
  const [open, setOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [points, setPoints] = useState(100);
  const [reason, setReason] = useState("Admin adjustment");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const adjust = async () => {
    if (!customerId.trim()) return toast.error("Customer ID required");
    setBusy(true);
    try {
      const r = await api.post(`/admin/loyalty/${customerId.trim()}/adjust?points=${Number(points)}&reason=${encodeURIComponent(reason)}`);
      setResult(r.data); toast.success("Loyalty adjusted");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <SectionCard className="p-6 max-w-lg">
      <div className="flex items-center gap-2 mb-1"><Gift className="h-5 w-5 text-primary" /><h3 className="font-semibold font-outfit">Adjust Loyalty Wallet</h3></div>
      <p className="text-sm text-muted-foreground mb-4">Credit or debit points for a customer (100 points = ₹1). Use a negative value to debit.</p>
      <div className="space-y-3">
        <div><Label>Customer ID</Label><Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="Customer user id" className="mt-1.5 font-mono" data-testid="loyalty-customer-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Points</Label><Input type="number" value={points} onChange={(e) => setPoints(e.target.value)} className="mt-1.5" data-testid="loyalty-points-input" /></div>
          <div><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" data-testid="loyalty-reason-input" /></div>
        </div>
        <Button onClick={adjust} disabled={busy} data-testid="loyalty-adjust-btn">{busy ? "Applying…" : "Apply adjustment"}</Button>
        {result && <div className="text-sm bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-lg px-3 py-2" data-testid="loyalty-result">New balance: <b>{result.points}</b> pts (₹{(result.points / 100).toFixed(2)})</div>}
      </div>
    </SectionCard>
  );
}

export default function Referrals() {
  return (
    <div>
      <PageHeader title="Referrals & Loyalty" subtitle="Monitor referrals and manage loyalty wallets" />
      <Tabs defaultValue="referrals">
        <TabsList data-testid="referrals-tabs">
          <TabsTrigger value="referrals" data-testid="referrals-tab-list">Referrals</TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="referrals-tab-loyalty">Loyalty</TabsTrigger>
        </TabsList>
        <TabsContent value="referrals" className="mt-4"><ReferralsTab /></TabsContent>
        <TabsContent value="loyalty" className="mt-4"><LoyaltyTab /></TabsContent>
      </Tabs>
    </div>
  );
}
